import "./Tooltip.scss";

import clsx from "clsx";
import { type ReactNode, forwardRef, useId } from "react";
import { ITooltip, Tooltip as ReactTooltip, TooltipRefProps } from "react-tooltip";

import { CircleInfoIcon } from "mds/icons";

export { type TooltipRefProps } from "react-tooltip";

export type TooltipAnchorProps = {
  "aria-describedby"?: string;
  "data-tooltip-delay-hide"?: number;
  "data-tooltip-delay-show"?: number;
  "data-tooltip-id"?: string;
  "data-tooltip-variant"?: "light";
};

export interface TooltipProps extends Omit<ITooltip, "id" | "content" | "children"> {
  content?: ReactNode;
  // TODO: we should remove the contentClassName prop so that all tooltips have the same styling.
  // This is currently only being used in AIAssistantDebugger, which is not user-facing.
  contentClassName?: string;
  wrapperClassName?: string;
  children?: ReactNode | ((anchorProps: TooltipAnchorProps) => ReactNode);
}

/**
 * Styled wrapper for react-tooltip.
 * Docs at https://react-tooltip.com/docs/getting-started
 *
 * Use {@link TooltipTrigger} when wrapping a non-focusable control (icon, text, etc.).
 *
 * To ensure that the tooltip will be read by screen readers, use a render-prop child when
 * wrapping a focusable control so anchor attrs (including aria-describedby) land on the
 * control itself:
 * <Tooltip content="…">{(tooltipAnchorProps) => <Button {...tooltipAnchorProps}>…</Button>}</Tooltip>
 */
export const Tooltip = forwardRef<TooltipRefProps, TooltipProps>(
  ({ contentClassName, wrapperClassName = "flex", children, content, ...props }, ref) => {
    const id = `tooltip-${useId()}`;

    if (!content) {
      return typeof children === "function"
        ? children({})
        : children || <CircleInfoIcon height="16" width="16" />;
    }

    const anchorProps: TooltipAnchorProps = {
      "aria-describedby": id,
      "data-tooltip-delay-hide": 0,
      "data-tooltip-delay-show": 0,
      "data-tooltip-id": id,
      "data-tooltip-variant": "light",
    };

    const hoverableElementContent =
      typeof children === "function"
        ? children(anchorProps)
        : children || <CircleInfoIcon height="16" width="16" />;

    return (
      <div className={clsx("mds-tooltip", wrapperClassName)}>
        {typeof children === "function" ? (
          hoverableElementContent
        ) : (
          <span
            aria-describedby={id}
            className="mds-tooltip-source"
            data-tooltip-delay-hide={0}
            data-tooltip-delay-show={0}
            data-tooltip-id={id}
            data-tooltip-variant="light"
          >
            {hoverableElementContent}
          </span>
        )}
        <ReactTooltip
          className={clsx("mds-tooltip-content z-[500]", contentClassName)}
          id={id}
          offset={8}
          disableStyleInjection
          noArrow
          {...props}
          globalCloseEvents={{ ...props.globalCloseEvents, escape: true }}
          ref={ref}
        >
          {content}
        </ReactTooltip>
      </div>
    );
  },
);

Tooltip.displayName = "Tooltip";
